import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { API, apiError, formatINR, formatDate } from "../lib/api";
import { ArrowLeft, Plus, X, BookOpen, FileText, FileSpreadsheet, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_PAY = { amount: "", mode: "Cash", date: new Date().toISOString().slice(0, 10), notes: "", direction: "credit" };

export default function VendorLedger() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // null | "new" | {txn}
  const [form, setForm] = useState(EMPTY_PAY);

  const load = useCallback(async () => {
    try { const r = await API.get(`/vendors/${id}/ledger`); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY_PAY }); setModal("new"); };

  const openEdit = (t) => {
    // PAYMENT or REFUND editable
    setForm({
      amount: t.credit || t.debit,
      mode: t.mode || "Cash",
      date: t.date,
      notes: (t.description || "").replace(/^(Payment|Refund) \([^)]+\)\s*/, ""),
      direction: t.direction || (t.type === "REFUND" ? "debit" : "credit"),
    });
    setModal(t);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, amount: parseFloat(form.amount) };
      if (modal === "new") {
        await API.post(`/vendors/${id}/payments`, body);
        toast.success("Payment recorded");
      } else {
        await API.put(`/vendor_transactions/${modal.id}`, body);
        toast.success("Payment updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delTxn = async (t) => {
    const isPurchase = t.type === "PURCHASE";
    const msg = isPurchase
      ? `This is a purchase entry (${t.ref}). Deleting it will remove the purchase, reverse stock and delete its payments. Continue?`
      : `Delete this ${t.type?.toLowerCase() || "entry"}?`;
    if (!window.confirm(msg)) return;
    try {
      await API.delete(`/vendor_transactions/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportLedger = (fmt) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/vendor_ledger/export?format=${fmt}&vendor_id=${id}`;
    window.open(url, "_blank");
  };

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;
  const { vendor, entries } = data;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="vendor-ledger-page">
      <Link to="/vendors" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#002FA7]">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Vendors
      </Link>

      <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-wrap items-start gap-6 justify-between">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#10B981] to-[#047857] text-white flex items-center justify-center">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <div className="overline mb-1">Vendor Ledger</div>
            <h1 className="font-display text-3xl font-bold">{vendor.name}</h1>
            <div className="text-sm text-slate-500 mt-1 space-x-3">
              {vendor.contact_person && <span>{vendor.contact_person}</span>}
              {vendor.phone && <span>· {vendor.phone}</span>}
              {vendor.gst && <span className="font-mono">· {vendor.gst}</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="overline">{vendor.balance > 0 ? "You Owe" : vendor.balance < 0 ? "Total Paid (Advance)" : "Balance"}</div>
          <div className={`font-display text-3xl font-bold ${vendor.balance > 0 ? "text-red-600" : vendor.balance < 0 ? "text-emerald-600" : "text-slate-400"}`}>
            {formatINR(Math.abs(vendor.balance))}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {vendor.balance > 0
              ? "Outstanding payable to this vendor"
              : vendor.balance < 0
              ? "Total payment made to vendor (advance / no invoice pending)"
              : "Fully settled"}
          </div>
          <div className="flex gap-2 mt-3 justify-end">
            <button onClick={openNew} className="btn-primary" data-testid="record-payment-btn"><Plus className="w-4 h-4" /> Record Payment</button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display font-semibold">Transaction History</div>
          <div className="flex gap-2">
            <button onClick={() => exportLedger("pdf")} className="btn-ghost text-xs"><FileText className="w-3.5 h-3.5" /> PDF</button>
            <button onClick={() => exportLedger("excel")} className="btn-ghost text-xs"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
          </div>
        </div>
        {entries.length === 0 ? (
          <div className="p-10 text-sm text-slate-500 text-center">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr>
                <th>Date</th><th>Type</th><th>Description</th>
                <th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th>
                <th className="text-right pr-4">Actions</th>
              </tr></thead>
              <tbody>
                {entries.map((t, i) => {
                  const canEdit = (t.type === "PAYMENT" || t.type === "REFUND") && t.id;
                  const canDelete = t.id && t.type !== "OPENING";
                  return (
                    <tr key={t.id || i} data-testid={`ledger-row-${t.id || i}`}>
                      <td className="text-sm whitespace-nowrap">{formatDate(t.date)}</td>
                      <td><span className={`tag ${t.type === "PURCHASE" ? "tag-info" : t.type === "PAYMENT" ? "tag-success" : t.type === "REFUND" ? "tag-warning" : "tag-muted"}`}>{t.type}</span></td>
                      <td className="text-slate-600">{t.description}</td>
                      <td className="text-right font-medium text-slate-900 tabular-nums whitespace-nowrap">{t.debit > 0 ? formatINR(t.debit) : "—"}</td>
                      <td className="text-right font-medium text-emerald-700 tabular-nums whitespace-nowrap">{t.credit > 0 ? formatINR(t.credit) : "—"}</td>
                      <td className="text-right font-semibold tabular-nums whitespace-nowrap">
                        {formatINR(t.balance)}
                        {typeof t.raw_balance === "number" && t.raw_balance < 0 && (
                          <div className="text-[10px] font-normal text-emerald-600 mt-0.5">advance {formatINR(-t.raw_balance)}</div>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          {canEdit && (
                            <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]" data-testid={`edit-txn-${t.id}`} title="Edit payment">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => delTxn(t)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600" data-testid={`del-txn-${t.id}`} title={t.type === "PURCHASE" ? "Delete purchase (reverses stock)" : "Delete"}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold">{modal === "new" ? "Record Payment" : "Edit Payment"}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <L label="Type *">
                <div className="grid grid-cols-2 gap-2" data-testid="payment-direction-toggle">
                  <button type="button" onClick={() => setForm({ ...form, direction: "credit" })}
                          data-testid="direction-credit"
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${form.direction === "credit" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    Credit · Paid to vendor
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, direction: "debit" })}
                          data-testid="direction-debit"
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${form.direction === "debit" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    Debit · Refund from vendor
                  </button>
                </div>
              </L>
              <L label="Amount (₹) *"><input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" className="inp" /></L>
              <L label="Mode">
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="inp">
                  {["Cash", "UPI", "Card", "Cheque", "Bank Transfer"].map((m) => <option key={m}>{m}</option>)}
                </select>
              </L>
              <L label="Date"><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="inp" /></L>
              <L label="Notes"><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="inp" placeholder="Cheque no / ref" /></L>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-payment-btn">
                {modal === "new" ? "Save Payment" : "Update Payment"}
              </button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.5rem .75rem;font-size:.875rem;outline:none}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}
function L({ label, children }) { return <label className="block"><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>; }
